import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-inline-otp',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="mt-2 p-3 rounded-xl bg-vivid-royal-50 dark:bg-vivid-royal-900 border border-vivid-royal-200 dark:border-vivid-royal-700 animate-fade-in">
      <p class="text-xs text-vivid-royal-500 dark:text-vivid-royal-300 mb-2">
        Código enviado a <b>{{ maskedTarget }}</b>
      </p>
      <div class="flex items-center gap-2">
        <div class="flex gap-1">
          @for (i of digits; track i) {
            <input
              [id]="'otp-' + type + '-' + i"
              type="text"
              maxlength="1"
              inputmode="numeric"
              class="w-9 h-9 text-center rounded-lg border text-sm font-semibold bg-white dark:bg-vivid-royal-800 text-vivid-royal-800 dark:text-vivid-royal-100 border-vivid-royal-300 dark:border-vivid-royal-600 outline-none focus:border-mustard-500 focus:ring-1 focus:ring-mustard-500/40"
              (keyup)="onKey($event, i)"
              (paste)="onPaste($event)"
            />
          }
        </div>
        <button
          [disabled]="code.length < 4 || verifying"
          (click)="verify()"
          class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-mustard-500 text-vivid-royal-800 disabled:bg-zinc-300 disabled:text-zinc-500 transition-colors">
          {{ verifying ? '...' : 'Verificar' }}
        </button>
        <button
          (click)="cancel()"
          class="px-2 py-1.5 text-xs text-vivid-royal-400 hover:text-red-500 transition-colors">
          ✕
        </button>
      </div>
      @if (error) {
        <p class="text-xs text-red-500 mt-1">{{ error }}</p>
      }
      <div class="flex items-center gap-2 mt-1">
        <p class="text-[10px] text-vivid-royal-400">
          @if (canResend) {
            <button class="text-mustard-600 font-semibold cursor-pointer" (click)="resendCode()">Reenviar código</button>
          } @else {
            Reenviar en {{ timer }}s
          }
        </p>
      </div>
    </div>
  `
})
export class InlineOtpComponent implements OnInit, OnDestroy {
  @Input() type: 'email' | 'phone' = 'email';
  @Input() target = '';
  @Output() verified = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() resend = new EventEmitter<void>();

  digits = [0, 1, 2, 3];
  code = '';
  verifying = false;
  error = '';
  timer = 30;
  canResend = false;
  private countdownId: any;

  get maskedTarget(): string {
    if (this.type === 'phone') {
      return '*** *** **' + this.target.slice(-2);
    }
    const [name, domain] = this.target.split('@');
    return name ? `${name[0]}${'*'.repeat(name.length - 1)}@${domain}` : this.target;
  }

  ngOnInit() { this.startTimer(); }
  ngOnDestroy() { clearInterval(this.countdownId); }

  startTimer() {
    this.timer = 30;
    this.canResend = false;
    this.countdownId = setInterval(() => {
      this.timer--;
      if (this.timer <= 0) { clearInterval(this.countdownId); this.canResend = true; }
    }, 1000);
  }

  onKey(event: KeyboardEvent, idx: number) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace') {
      if (!input.value && idx > 0) {
        const prev = document.getElementById(`otp-${this.type}-${idx - 1}`) as HTMLInputElement;
        prev?.focus();
      }
    } else if (/^\d$/.test(input.value)) {
      if (idx < 3) {
        const next = document.getElementById(`otp-${this.type}-${idx + 1}`) as HTMLInputElement;
        next?.focus();
      }
    } else {
      input.value = '';
    }
    this.rebuildCode();
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 4) || '';
    pasted.split('').forEach((ch, i) => {
      const el = document.getElementById(`otp-${this.type}-${i}`) as HTMLInputElement;
      if (el) el.value = ch;
    });
    this.rebuildCode();
  }

  private rebuildCode() {
    this.code = this.digits.map(i => {
      const el = document.getElementById(`otp-${this.type}-${i}`) as HTMLInputElement;
      return el?.value || '';
    }).join('');
  }

  verify() {
    this.verifying = true;
    this.error = '';
    this.verified.emit(this.code);
  }

  setError(msg: string) { this.error = msg; this.verifying = false; }
  setSuccess() { this.verifying = false; }

  cancel() { this.cancelled.emit(); }

  resendCode() {
    this.startTimer();
    this.resend.emit();
  }
}
